import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import useZoneIsActive from "@store/Framework/Common/Hooks/useZoneIsActive";
import breakpointValues from "@store/Framework/Common/Utilities/breakpointValues";
import { pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";
import Heading, { HeadingPresentationProps } from "@store/Library/Components/Toolkit/Heading";
import LinkBack, { LinkBackPresentationProps, LinkBackProps } from "@store/Library/Components/Toolkit/LinkBack";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import React, { FC, ReactNode } from "react";
import { css } from "styled-components";

const zoneName = "Message";

const wrapperStyles = () => css`
    ${x => spacingStyles(x.theme.design.typography.spacing.base, x.theme.design.typography.spacing.base)}
`;

export interface OrderHeaderProps extends ExpandStyles<OrderHeaderStyles> {
    contentId?: string;
    title: string;
    message?: string | ReactNode;
    linkBack?: LinkBackProps["pageLink"];
    linkBackOnClick?: LinkBackProps["onClick"];
    children?: React.ReactNode;
}

export interface OrderHeaderStyles {
    wrapper?: InjectableCss;
    heading?: HeadingPresentationProps;
    messageWrapper?: InjectableCss;
    zoneWrapper?: InjectableCss;
    linkBack?: LinkBackPresentationProps;
}

export const orderHeaderStyles: OrderHeaderStyles = {
    wrapper: {
        css: css`
            ${x => breakpointValues(x.theme.design.layout.spacing.narrowContent, "margin-bottom")}

            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    heading: {
        variant: "h1",
        extendedStyles: {
            title: {
                css: css`
                    margin: 0;
                `,
            },
            flexWrapper: {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            border-bottom: 3px solid ${getColor("common.border")};
                            ${breakpointValues(pxToRemArray([16, null, 20, null, 24]), "padding-bottom")}
                        `,
                    },
                },
            },
        },
    },
    messageWrapper: {
        css: wrapperStyles(),
    },
    zoneWrapper: {
        css: wrapperStyles(),
    },
};

const OrderHeader: FC<OrderHeaderProps> = ({
    children,
    extendedStyles,
    overridedStyles,
    title,
    contentId,
    message,
    linkBack,
    linkBackOnClick,
}) => {
    const styles = useExpandProps(orderHeaderStyles, extendedStyles, overridedStyles);
    const zoneIsActive = useZoneIsActive(contentId || "", zoneName);

    return (
        <StyledWrapper {...styles.wrapper}>
            <LinkBack pageLink={linkBack} onClick={linkBackOnClick} {...styles.linkBack} />
            <Heading title={title} {...styles.heading}>
                {children}
            </Heading>
            {message ? <StyledWrapper {...styles.messageWrapper}>{message}</StyledWrapper> : null}
            {contentId && zoneIsActive ? (
                <StyledWrapper {...styles.zoneWrapper}>
                    <Zone contentId={contentId} zoneName={zoneName} />
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

export default OrderHeader;
